import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, Subject, catchError, switchMap, take, throwError, of } from 'rxjs';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '@shared/interfaces/wrap-api-response.interface';
import { LoginResponse } from 'src/app/login/data-access/interfaces/login.interface';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
	private http = inject(HttpClient);
	private router = inject(Router);

	private isRefreshing = false;
	private refreshSubject: Subject<string> | null = null;

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		return next.handle(req).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status === 401) {
					const isAuthEndpoint = /\/user\/(login|refresh-token)$/.test(req.url);

					// Si no es un endpoint de autenticación, manejar el 401
					if (!isAuthEndpoint) {
						const refreshToken = localStorage.getItem('refreshToken');

						// Si hay refreshToken, intentar renovar
						if (refreshToken) {
							return this.handle401Error(req, next);
						}

						// Si no hay refreshToken, cerrar sesión
						this.forceLogout();
						return throwError(() => error);
					}
				}
				return throwError(() => error);
			})
		);
	}

	private handle401Error(originalReq: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshSubject = new Subject<string>();

			const refreshToken = localStorage.getItem('refreshToken') || '';
			return this.refreshAccessToken(refreshToken).pipe(
				switchMap((newAccessToken) => {
					this.isRefreshing = false;
					if (this.refreshSubject) {
						this.refreshSubject.next(newAccessToken);
						this.refreshSubject.complete();
						this.refreshSubject = null;
					}

					const cloned = this.addAuthHeader(originalReq, newAccessToken);
					return next.handle(cloned);
				}),
				catchError((refreshError) => {
					this.isRefreshing = false;
					if (this.refreshSubject) {
						this.refreshSubject.error(refreshError);
						this.refreshSubject = null;
					}
					this.forceLogout();
					return throwError(() => refreshError);
				})
			);
		} else {
			if (!this.refreshSubject) {
				// Si por alguna razón el Subject no existe, crear uno nuevo
				this.refreshSubject = new Subject<string>();
			}
			return this.refreshSubject.pipe(
				take(1),
				switchMap((token) => {
					const cloned = this.addAuthHeader(originalReq, token);
					return next.handle(cloned);
				}),
				catchError((err) => {
					this.forceLogout();
					return throwError(() => err);
				})
			);
		}
	}

	private addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
		return req.clone({
			setHeaders: { Authorization: `Bearer ${token}` }
		});
	}

	private refreshAccessToken(refreshToken: string): Observable<string> {
		const url = `${environment.API_DOMAIN}${environment.SERVICES.SECURITY}/user/refresh-token`;
		const body = { refreshToken };

		return this.http.post<ApiResponse<LoginResponse>>(url, body).pipe(
			switchMap((resp) => {
				// Verificar que resultCode sea 0 (éxito) y que tenga accessToken
				if (resp.resultCode === 0 && resp.result?.accessToken) {
					const newAccessToken = resp.result.accessToken;
					const newRefreshToken = resp.result.refreshToken;

					// Guardar los nuevos tokens
					localStorage.setItem('token', newAccessToken);
					if (newRefreshToken) {
						localStorage.setItem('refreshToken', newRefreshToken);
					}

					return of(newAccessToken);
				}

				// Si resultCode no es 0 o no hay accessToken, es un error
				return throwError(() => new Error('Refresh token response not successful'));
			}),
			catchError((error) => {
				// Cualquier error HTTP o de negocio, se considera fallo
				return throwError(() => error);
			})
		);
	}

	private forceLogout(): void {
		localStorage.removeItem('token');
		localStorage.removeItem('userInfo');
		localStorage.removeItem('refreshToken');
		this.router.navigate(['/login']);
	}
}
